import { useGetPlaceholdersSalvaInTabella } from '@framework/api-client/api/inquiries/placeholder-controller/placeholder-controller.ts';
import { type PlaceholderDto } from '@framework/api-client/api/inquiries/schemas/placeholderDto.ts';
import {
  type SectionType,
  useSectionType,
} from '@framework/engine/schema/sections/SectionTypeContext.tsx';
import { useMemo } from 'react';

/**
 * Sezioni per cui il BE espone i campi salvabili: i valori coincidono con
 * quelli attesi dal parametro `tipo` dell'API.
 */
const SUPPORTED_SECTION_TYPES: SectionType[] = ['FRONTOFFICE', 'BACKOFFICE'];

export interface ITableSavePresetField {
  /** Etichetta mostrata nella select, fornita dal BE. */
  label: string;
  /** Placeholder salvato in `name`, usato anche come value dell'opzione. */
  placeholder: string;
}

export interface IUseTableSavePresetFieldsResult {
  presets: PlaceholderDto[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Recupera i campi predefiniti ("preset") proposti nella configurazione della
 * direttiva `tableSave`, filtrati per il tipo della sezione corrente.
 *
 * Contratto atteso (FWP-4001):
 * `GET /framework-cfg-domande/placeholders-salva-in-tabella?tipo={FRONTOFFICE|BACKOFFICE}`
 * risponde con `PlaceholderDto[]` (`label`, `placeholder`), già ordinato per
 * etichetta crescente. Nello Swagger `tipo` è una stringa libera: i valori
 * accettati seguono la convenzione degli altri endpoint dello stesso
 * microservizio.
 *
 * Questo file è l'unico punto di contatto con l'API: se il contratto cambia
 * (path, parametro di filtro o forma del DTO) va adeguato solo qui, senza
 * toccare la UI di configurazione.
 */
export function useTableSavePresetFields(): IUseTableSavePresetFieldsResult {
  const { sectionType } = useSectionType();
  const enabled = SUPPORTED_SECTION_TYPES.includes(sectionType);

  const { data, isLoading, isError, refetch } =
    useGetPlaceholdersSalvaInTabella(
      { tipo: sectionType },
      { query: { enabled } },
    );

  // I campi del DTO sono entrambi opzionali: scartiamo le voci incomplete,
  // che non permetterebbero comunque di configurare nulla. L'ordinamento per
  // etichetta è già applicato dal BE, quindi lo manteniamo così com'è.
  const presets = useMemo(() => {
    if (!data) return [];
    return data.filter(
      (preset): preset is ITableSavePresetField =>
        typeof preset.label === 'string' &&
        typeof preset.placeholder === 'string',
    );
  }, [data]);

  return {
    presets,
    isLoading: enabled && isLoading,
    isError,
    refetch,
  };
}
